import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ImgTextCard from "../cards/ImgTextCard";
import SEO from "../common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const Discography = ({ cds, renderBody }) => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("all");

  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "Discography" : "Discografia";
  const description = isEnglish
    ? "Explore the extensive discography of the Nuova Orchestra da Camera Ferruccio Busoni: 35+ CDs recorded with Brilliant Classics, Warner, Amadeus, and more."
    : "Esplora la ricca discografia della Nuova Orchestra da Camera Ferruccio Busoni: oltre 35 CD incisi per Brilliant Classics, Warner Classics, Amadeus, Dynamic e altre prestigiose etichette.";

  const filterOptions = [
    { id: "all", label_it: "Tutti i CD", label_en: "All CDs" },
    { id: "brilliant", label_it: "Brilliant Classics", label_en: "Brilliant Classics" },
    { id: "amadeus", label_it: "Amadeus", label_en: "Amadeus" },
    { id: "dynamic", label_it: "Dynamic", label_en: "Dynamic" },
    { id: "warner", label_it: "Warner / Suonare", label_en: "Warner / Suonare" },
  ];

  const filteredCds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return cds.filter((cd) => {
      // Label filter check
      if (selectedLabel !== "all") {
        const fullText = `${cd.title} ${cd.details} ${cd.buy_link || ""}`.toLowerCase();
        if (selectedLabel === "brilliant" && !fullText.includes("brilliant")) return false;
        if (selectedLabel === "amadeus" && !fullText.includes("amadeus")) return false;
        if (selectedLabel === "dynamic" && !fullText.includes("dynamic")) return false;
        if (
          selectedLabel === "warner" &&
          !fullText.includes("warner") &&
          !fullText.includes("suonare")
        )
          return false;
      }

      // Query filter check
      if (!q) return true;
      const matchTitle = cd.title && cd.title.toLowerCase().includes(q);
      const matchDetails = cd.details && cd.details.toLowerCase().includes(q);
      return matchTitle || matchDetails;
    });
  }, [cds, searchQuery, selectedLabel]);

  return (
    <>
      <SEO title={title} description={description} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* Header intro */}
          <div className="text-center mb-4">
            <p className="text-muted max-w-700 mx-auto mb-0">
              {isEnglish
                ? "Over 35 international CD releases featuring world premiere recordings, major soloists, and acclaimed critical editions."
                : "Oltre 35 incisioni discografiche internazionali con prime registrazioni assolute, solisti di chiara fama e prestigiose recensioni."}
            </p>
          </div>

          {/* Search bar & filter pills */}
          <div className="interactive-filter-card p-4 rounded-4 shadow-sm mb-5 bg-white border">
            <div className="row g-3 align-items-center">
              <div className="col-lg-6">
                <div className="modern-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder={
                      isEnglish
                        ? "Search by composer, soloist, or work (e.g. Mozart, Marzadori, Viotti)..."
                        : "Cerca per compositore, solista o brano (es. Mozart, Marzadori, Viotti)..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Cerca brano o compositore"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="modern-search-clear"
                      onClick={() => setSearchQuery("")}
                      aria-label="Cancella ricerca"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Pills */}
              <div className="col-lg-6">
                <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                  {filterOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`filter-pill-btn ${
                        selectedLabel === opt.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedLabel(opt.id)}
                    >
                      {isEnglish ? opt.label_en : opt.label_it}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results count counter */}
            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top small text-muted">
              <span>
                {isEnglish
                  ? `Showing ${filteredCds.length} albums`
                  : `Visualizzazione di ${filteredCds.length} album`}
              </span>
              {(searchQuery || selectedLabel !== "all") && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedLabel("all");
                  }}
                >
                  {isEnglish ? "Reset filters" : "Azzera filtri"}
                </button>
              )}
            </div>
          </div>

          {/* CD Cards List */}
          {filteredCds.length > 0 ? (
            <div className="discography-grid">
              {filteredCds.map((cd) => (
                <ImgTextCard
                  key={cd.id}
                  id={cd.id}
                  title={cd.title}
                  img={cd.img}
                  details={cd.details}
                  buy_link={cd.buy_link}
                  img_width={100}
                  text_instead_of_buy_link={cd.text_instead_of_buy_link}
                  renderBody={renderBody}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
              <p className="text-muted mb-0 fs-5">
                {isEnglish
                  ? "No albums found matching your search."
                  : "Nessun album trovato per i criteri specificati."}
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-3 rounded-pill px-3"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedLabel("all");
                }}
              >
                {isEnglish ? "Reset search" : "Mostra tutti"}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Discography;
